import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Hash, ExternalLink, Loader2 } from "lucide-react";
import PageHeader from "@/layout/PageHeader";
import { API, authHeaders } from "@/lib/api";


async function fetchWeek(date) {
  const r = await fetch(`${API}/weekly-numbers/week/${date}`, { headers: authHeaders() });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
}


function CardTile({ card }) {
  const cascade = card.tier_cascade || {};
  return (
    <div className="hm-surface overflow-hidden" data-testid={`number-card-${card.card_index}`}>
      <div style={{ background: "#1A2B4A" }} className="flex items-center justify-center p-6">
        <img
          src={`${API}/weekly-numbers/card/${card.id}/image`}
          alt={card.headline_stat}
          className="w-full max-w-[420px] rounded-md shadow-lg"
          loading="lazy"
        />
      </div>
      <div className="p-5 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-hm-accent">
              Card {card.card_index} · {card.eyebrow}
            </div>
            <div className="mt-1 text-lg font-semibold text-hm-fg">{card.headline_stat}</div>
          </div>
          <span className="mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-hm-bg-1 text-hm-fg-subtle">
            Tier {card.company_tier}
          </span>
        </div>
        <div className="text-sm text-hm-fg-muted leading-relaxed">{card.context_sentence}</div>
        <div className="text-sm font-medium text-hm-accent">{card.tagline}</div>
        <div className="pt-2 border-t border-hm-border mono text-[11px] text-hm-fg-muted space-y-1">
          <div className="truncate"><strong className="text-hm-fg">{card.company_name}</strong></div>
          <a href={card.source_url} target="_blank" rel="noreferrer"
             className="text-hm-accent hover:underline inline-flex items-center gap-1 break-all">
            <ExternalLink className="h-3 w-3 shrink-0" /> {card.source_name} · {card.source_date}
          </a>
        </div>
      </div>
    </div>
  );
}


export default function WeeklyNumberDetail() {
  const { date } = useParams();
  const { data, isPending, isError, error } = useQuery({
    queryKey: ["weekly-number", date],
    queryFn: () => fetchWeek(date),
    staleTime: 60_000,
  });

  if (isPending) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-hm-fg-subtle" /></div>;
  }
  if (isError) {
    return <div className="hm-surface p-6" data-testid="number-detail-error">
      <p className="text-sm text-hm-fg">Failed to load: {error?.message}</p>
    </div>;
  }
  const cards = data?.cards || [];
  const cascade = cards[0]?.tier_cascade || {};

  return (
    <div data-testid="number-detail">
      <PageHeader
        title={`Weekly By The Numbers`}
        subtitle={`Week of ${data?.week_date} · ${cards.length}/3 cards`}
        actions={
          <Link to="/weekly-numbers"
                className="inline-flex items-center gap-1.5 text-xs text-hm-fg-muted hover:text-hm-fg">
            <ArrowLeft className="h-3.5 w-3.5" /> Back
          </Link>
        }
      />
      <div className="grid lg:grid-cols-[1fr_0.4fr] gap-6">
        <div className="grid md:grid-cols-1 lg:grid-cols-1 gap-6" data-testid="number-cards-grid">
          {cards.map((c) => <CardTile key={c.id} card={c} />)}
        </div>
        <div className="hm-surface p-6 h-max" data-testid="number-cascade-stats">
          <div className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle mb-4">
            Tier cascade & metadata
          </div>
          <dl className="space-y-3 text-xs">
            <div className="flex justify-between"><dt className="text-hm-fg-muted">Tier 1 selected</dt><dd className="text-hm-fg font-medium mono">{cascade.tier1_selected ?? 0}</dd></div>
            <div className="flex justify-between"><dt className="text-hm-fg-muted">Tier 2 selected</dt><dd className="text-hm-fg font-medium mono">{cascade.tier2_selected ?? 0}</dd></div>
            <div className="flex justify-between"><dt className="text-hm-fg-muted">Tier 3 selected</dt><dd className="text-hm-fg font-medium mono">{cascade.tier3_selected ?? 0}</dd></div>
            <div className="pt-3 border-t border-hm-border">
              <div className="flex justify-between"><dt className="text-hm-fg-muted">Tier 1 available</dt><dd className="text-hm-fg mono">{cascade.tier1_available ?? "—"}</dd></div>
              <div className="flex justify-between"><dt className="text-hm-fg-muted">Tier 2 available</dt><dd className="text-hm-fg mono">{cascade.tier2_available ?? "—"}</dd></div>
              <div className="flex justify-between"><dt className="text-hm-fg-muted">Tier 3 available</dt><dd className="text-hm-fg mono">{cascade.tier3_available ?? "—"}</dd></div>
              <div className="flex justify-between"><dt className="text-hm-fg-muted">Diversity cats</dt><dd className="text-hm-fg mono">{cascade.diversity_categories ?? "—"}</dd></div>
            </div>
          </dl>
          <div className="mt-6 pt-6 border-t border-hm-border">
            <a href={`${API}/weekly-numbers/week/${data?.week_date}/preview-email`}
               target="_blank" rel="noreferrer"
               data-testid="number-preview-email-link"
               className="inline-flex items-center gap-1.5 text-xs text-hm-accent hover:underline">
              <ExternalLink className="h-3.5 w-3.5" /> Open email preview
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
